export interface SystemMessageGroupType {
  id: number;
  description: string;
  key: string;
  name: string;
}

export interface SystemMessageResponseType {
  id: number;
  group_id: number;
  group: SystemMessageGroupType;
  name: string;
  key: string;
  description: string;
  subject: string;
  message: string;
  email_template: string;
  send_email: boolean;
  send_notification: boolean;
  send_sms: boolean;
  delay_while_on_call: boolean;
  send_sms_as_primary: boolean;
}

export interface RejectModalDataType {
  system_message_id: number;
  subject: string;
  message: string;
  send_message: boolean;
}

export interface UserDataType {
  created_at: string;
  display_name: string | null;
  display_name_rejected: boolean;
  email_address: string;
  first_name: string;
  id: number;
  last_name: string;
  proposed_display_name: string | null;
  test_user: boolean;
}

export interface ListenerDataType {
  about_me: string | null;
  about_me_rejected: boolean;
  can_take_calls: boolean;
  earnings: number;
  id: number;
  impact_score: number;
  is_active: boolean;
  is_peer_active: boolean;
  listener_tags: string[];
  notes: string;
  profile_photo_approved: boolean;
  profile_photo_file_url: string;
  proposed_about_me: string | null;
  state: StateType;
  user: UserDataType;
  client_id: number | null;
  domain_id: number | null;
  resource_id: number | null;
}

export interface CountResponseType {
  count: number;
}

// Enum fot state values, as they are typed in the backend
export enum StateType {
  ALABAMA = 'alabama',
  ALASKA = 'alaska',
  ARIZONA = 'arizona',
  ARKANSAS = 'arkansas',
  CALIFORNIA = 'california',
  COLORADO = 'colorado',
  CONNECTICUT = 'connecticut',
  DC = 'dc',
  DELAWARE = 'delaware',
  FLORIDA = 'florida',
  GEORGIA = 'georgia',
  HAWAII = 'hawaii',
  IDAHO = 'idaho',
  ILLINOIS = 'illinois',
  INDIANA = 'indiana',
  IOWA = 'iowa',
  KANSAS = 'kansas',
  KENTUCKY = 'kentucky',
  LOUISIANA = 'louisiana',
  MAINE = 'maine',
  MARYLAND = 'maryland',
  MASSACHUSETTS = 'massachusetts',
  MICHIGAN = 'michigan',
  MINNESOTA = 'minnesota',
  MISSISSIPPI = 'mississippi',
  MISSOURI = 'missouri',
  MONTANA = 'montana',
  NEBRASKA = 'nebraska',
  NEVADA = 'nevada',
  NEW_HAMPSHIRE = 'new_hampshire',
  NEW_JERSEY = 'new_jersey',
  NEW_MEXICO = 'new_mexico',
  NEW_YORK = 'new_york',
  NORTH_CAROLINA = 'north_carolina',
  NORTH_DAKOTA = 'north_dakota',
  OHIO = 'ohio',
  OKLAHOMA = 'oklahoma',
  OREGON = 'oregon',
  PENNSYLVANIA = 'pennsylvania',
  RHODE_ISLAND = 'rhode_island',
  SOUTH_CAROLINA = 'south_carolina',
  SOUTH_DAKOTA = 'south_dakota',
  TENNESSEE = 'tennessee',
  TEXAS = 'texas',
  UTAH = 'utah',
  VERMONT = 'vermont',
  VIRGINIA = 'virginia',
  WASHINGTON = 'washington',
  WEST_VIRGINIA = 'west_virginia',
  WISCONSIN = 'wisconsin',
  WYOMING = 'wyoming',
  AMERICAN_SAMOA = 'american_samoa',
  GUAM = 'guam',
  NORTHERN_MARIANA_ISLANDS = 'northern_mariana_islands',
  PUERTO_RICO = 'puerto_rico',
  VIRGIN_ISLANDS = 'virgin_islands',
}

// Map of state values to display in the dropdown, while sending the state value to the backend
export const STATE_DISPLAY_MAP: Record<StateType, string> = {
  [StateType.ALABAMA]: 'AL - Alabama',
  [StateType.ALASKA]: 'AK - Alaska',
  [StateType.ARIZONA]: 'AZ - Arizona',
  [StateType.ARKANSAS]: 'AR - Arkansas',
  [StateType.CALIFORNIA]: 'CA - California',
  [StateType.COLORADO]: 'CO - Colorado',
  [StateType.CONNECTICUT]: 'CT - Connecticut',
  [StateType.DC]: 'DC - District of Columbia',
  [StateType.DELAWARE]: 'DE - Delaware',
  [StateType.FLORIDA]: 'FL - Florida',
  [StateType.GEORGIA]: 'GA - Georgia',
  [StateType.HAWAII]: 'HI - Hawaii',
  [StateType.IDAHO]: 'ID - Idaho',
  [StateType.ILLINOIS]: 'IL - Illinois',
  [StateType.INDIANA]: 'IN - Indiana',
  [StateType.IOWA]: 'IA - Iowa',
  [StateType.KANSAS]: 'KS - Kansas',
  [StateType.KENTUCKY]: 'KY - Kentucky',
  [StateType.LOUISIANA]: 'LA - Louisiana',
  [StateType.MAINE]: 'ME - Maine',
  [StateType.MARYLAND]: 'MD - Maryland',
  [StateType.MASSACHUSETTS]: 'MA - Massachusetts',
  [StateType.MICHIGAN]: 'MI - Michigan',
  [StateType.MINNESOTA]: 'MN - Minnesota',
  [StateType.MISSISSIPPI]: 'MS - Mississippi',
  [StateType.MISSOURI]: 'MO - Missouri',
  [StateType.MONTANA]: 'MT - Montana',
  [StateType.NEBRASKA]: 'NE - Nebraska',
  [StateType.NEVADA]: 'NV - Nevada',
  [StateType.NEW_HAMPSHIRE]: 'NH - New Hampshire',
  [StateType.NEW_JERSEY]: 'NJ - New Jersey',
  [StateType.NEW_MEXICO]: 'NM - New Mexico',
  [StateType.NEW_YORK]: 'NY - New York',
  [StateType.NORTH_CAROLINA]: 'NC - North Carolina',
  [StateType.NORTH_DAKOTA]: 'ND - North Dakota',
  [StateType.OHIO]: 'OH - Ohio',
  [StateType.OKLAHOMA]: 'OK - Oklahoma',
  [StateType.OREGON]: 'OR - Oregon',
  [StateType.PENNSYLVANIA]: 'PA - Pennsylvania',
  [StateType.RHODE_ISLAND]: 'RI - Rhode Island',
  [StateType.SOUTH_CAROLINA]: 'SC - South Carolina',
  [StateType.SOUTH_DAKOTA]: 'SD - South Dakota',
  [StateType.TENNESSEE]: 'TN - Tennessee',
  [StateType.TEXAS]: 'TX - Texas',
  [StateType.UTAH]: 'UT - Utah',
  [StateType.VERMONT]: 'VT - Vermont',
  [StateType.VIRGINIA]: 'VA - Virginia',
  [StateType.WASHINGTON]: 'WA - Washington',
  [StateType.WEST_VIRGINIA]: 'WV - West Virginia',
  [StateType.WISCONSIN]: 'WI - Wisconsin',
  [StateType.WYOMING]: 'WY - Wyoming',
  [StateType.AMERICAN_SAMOA]: 'AS - American Samoa',
  [StateType.GUAM]: 'GU - Guam',
  [StateType.NORTHERN_MARIANA_ISLANDS]: 'MP - Northern Mariana Islands',
  [StateType.PUERTO_RICO]: 'PR - Puerto Rico',
  [StateType.VIRGIN_ISLANDS]: 'VI - Virgin Islands',
};
